import { isCompatibleMarketplaceProduct } from '@atlassian/mpac-services-client';
import { foldMarketplaceProduct } from '@atlassian/mpac-site-selection';
import { type MarketplaceStoreEditionType } from '@atlassian/mpac-types/graphql-gateway-types';
import queryString from 'query-string';

import BootstrapConfig from '../../../../../../bootstrapConfig';

import type { CompatibleProduct } from '@atlassian/mpac-app-listing';
import type {
  MarketplaceProduct as MarketplaceProductType,
  SelectedSiteAndProduct,
} from '@atlassian/mpac-site-selection';

type MarketplaceCompatibleProduct = CompatibleProduct & {
  id: MarketplaceProductType;
};

type UserInfo = {
  isForEndUser: boolean;
  userEmail: string | null | undefined;
};

export const onlyCompatibleProducts = (
  compatibleProducts: ReadonlyArray<CompatibleProduct>
): MarketplaceCompatibleProduct[] =>
  // @ts-expect-error [MC-2850] - TS2322 - Type 'AppListingCompatibleProductFragment[]' is not assignable to type 'JiraConfluenceCompatibleProduct[]'.
  compatibleProducts.filter((product) =>
    isCompatibleMarketplaceProduct(product.id as MarketplaceProductType)
  );

const getEmceeAppListingPageLink = (
  appKey: string,
  { site, product }: SelectedSiteAndProduct,
  { userEmail }: UserInfo,
  additionalQueryParams: {
    installDialogOpen?: boolean;
    requestDialogOpen?: boolean;
    directInstall?: boolean;
    plan?: MarketplaceStoreEditionType;
    selectedUserCount?: number;
  }
) => {
  const queryParamString = queryString.stringify({ source: 'mpac', ...additionalQueryParams });
  const deeplink = `app/${appKey}?${queryParamString}`;
  const { identityBaseUrl } = BootstrapConfig.get();
  const identityUrlQueryParamWithJiraPathString = queryString.stringify({
    application: 'mpac',
    login_hint: userEmail,
    prompt: 'none',
    continue: `${site.url}/jira/marketplace/discover/${deeplink}`,
  });
  const identityQueryUrlParamWithConfluencePathString = queryString.stringify({
    application: 'mpac',
    login_hint: userEmail,
    prompt: 'none',
    continue: `${site.url}/wiki/marketplace/discover/${deeplink}`,
  });
  const identityQueryUrlParamWithCompassPathString = queryString.stringify({
    application: 'mpac',
    login_hint: userEmail,
    prompt: 'none',
    continue: `${site.url}/compass/marketplace/discover/${deeplink}`,
  });
  return `${identityBaseUrl}${foldMarketplaceProduct(product)(
    () => `/login?${identityUrlQueryParamWithJiraPathString}`,
    () => `/login?${identityQueryUrlParamWithConfluencePathString}`,
    () => `/login?${identityQueryUrlParamWithCompassPathString}`
  )}`;
};

export const getEmceeAppListingInstallLink = (
  appKey: string,
  siteAndProduct: SelectedSiteAndProduct,
  userEmail: string | null | undefined,
  installDialogOpen = true,
  directInstall = false,
  selectedAppPlan?: MarketplaceStoreEditionType,
  selectedUserCount?: number
) => {
  return getEmceeAppListingPageLink(
    appKey,
    siteAndProduct,
    { isForEndUser: false, userEmail },
    {
      ...(installDialogOpen ? { installDialogOpen: true } : {}),
      ...(selectedAppPlan ? { plan: selectedAppPlan } : {}),
      ...(directInstall ? { directInstall: true } : {}),
      ...(selectedUserCount ? { selectedUserCount } : {}),
    }
  );
};

export const getEmceeAppListingRequestAppLink = (
  appKey: string,
  siteAndProduct: SelectedSiteAndProduct,
  userEmail: string | null | undefined,
  selectedAppPlan?: MarketplaceStoreEditionType
) => {
  return getEmceeAppListingPageLink(
    appKey,
    siteAndProduct,
    { isForEndUser: true, userEmail },
    {
      requestDialogOpen: true,
      ...(selectedAppPlan ? { plan: selectedAppPlan } : {}),
    }
  );
};

export const getInstalledAppProductLink = (url: string, userEmail?: string | null): string => {
  const { identityBaseUrl } = BootstrapConfig.get();
  const identityUrlPathString = queryString.stringify({
    application: 'mpac',
    login_hint: userEmail,
    prompt: 'none',
    continue: url,
  });

  return `${identityBaseUrl}/login?${identityUrlPathString}`;
};
